import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2, Search, Mail, Phone, MapPin, ExternalLink, ArrowLeft,
  RefreshCw, Download, Globe, Users, Filter, X, MessageSquare,
  Sparkles, Send, Wand2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  listProspects, getProspect, prospectStats, addProspectNote,
  setProspectStatus, recrawlProspect, downloadProspectsCsv,
  enrichProspect, enrichMissingProspects,
  patchOutreachDraft, sendOutreach,
} from "@/lib/api";

const STATUS_TONES = {
  new: "bg-sky-50 text-sky-700 ring-sky-200",
  contacted: "bg-amber-50 text-amber-700 ring-amber-200",
  replied: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  qualified: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  won: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  lost: "bg-slate-100 text-slate-500 ring-slate-200",
  archived: "bg-slate-100 text-slate-500 ring-slate-200",
};

const STATUSES = ["new", "contacted", "replied", "qualified", "won", "lost", "archived"];

function StatChip({ label, value, tone = "bg-slate-100 text-slate-700" }) {
  return (
    <div className={`rounded-xl px-4 py-3 ring-1 ring-inset ${tone}`}
         data-testid={`prospect-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-2xl font-semibold tabular-nums">{value ?? "—"}</div>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const tone = STATUS_TONES[status] || STATUS_TONES.new;
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ring-1 ring-inset ${tone}`}>
      {status || "new"}
    </span>
  );
}

export default function AdminProspects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [hasEmail, setHasEmail] = useState("");
  const [heat, setHeat] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [enrichingAll, setEnrichingAll] = useState(false);
  const [outreachDirty, setOutreachDirty] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user === null || (user && user.role !== "admin")) navigate("/login");
  }, [user, navigate]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const [items, s] = await Promise.all([
        listProspects({
          q: q || undefined,
          status: status || undefined,
          source: source || undefined,
          has_email: hasEmail === "" ? undefined : hasEmail === "yes",
          heat: heat || undefined,
          sort: "heat",
          limit: 100,
        }),
        prospectStats(),
      ]);
      setRows(items);
      setStats(s);
    } catch (e) {
      setRows({ items: [], total: 0 });
      toast.error("Could not load prospects");
    }
  }, [q, status, source, hasEmail, heat]);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  const openDetail = async (id) => {
    setSelected(id);
    setDetail(null);
    try {
      setDetail(await getProspect(id));
    } catch {
      toast.error("Could not load prospect");
      setSelected(null);
    }
  };

  const closeDetail = () => { setSelected(null); setDetail(null); setNote(""); };

  const doAddNote = async () => {
    if (!note.trim() || !selected) return;
    try {
      const r = await addProspectNote(selected, note.trim());
      setDetail((d) => d ? { ...d, notes: [...(d.notes || []), r.note] } : d);
      setNote("");
      toast.success("Note added");
    } catch { toast.error("Could not save note"); }
  };

  const changeStatus = async (id, newStatus) => {
    setBusyId(id);
    try {
      await setProspectStatus(id, newStatus);
      setRows((r) => r ? {
        ...r,
        items: r.items.map((x) => x.id === id ? { ...x, status: newStatus } : x),
      } : r);
      if (detail?.id === id) setDetail({ ...detail, status: newStatus });
      toast.success(`Marked ${newStatus}`);
      prospectStats().then(setStats).catch(() => {});
    } catch { toast.error("Could not update status"); }
    finally { setBusyId(null); }
  };

  const doRecrawl = async (id) => {
    setBusyId(id);
    try {
      await recrawlProspect(id);
      toast.success("Recrawl started — refresh in a few seconds");
      setTimeout(() => load(), 2500);
    } catch { toast.error("Recrawl failed"); }
    finally { setBusyId(null); }
  };

  const doEnrich = async (id) => {
    setBusyId(id);
    try {
      await enrichProspect(id, true);
      toast.success("Enriched — reloading");
      const fresh = await getProspect(id);
      setDetail(fresh);
      // Also update row inline
      setRows((r) => r ? {
        ...r,
        items: r.items.map((x) => x.id === id ? { ...x, ai_hook: fresh.ai_hook } : x),
      } : r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrichment failed");
    } finally { setBusyId(null); }
  };

  const doEnrichAllMissing = async () => {
    setEnrichingAll(true);
    try {
      const r = await enrichMissingProspects(25);
      toast.success(`Enriched ${r.enriched}/${r.processed} prospects`);
      await load();
    } catch { toast.error("Bulk enrichment failed"); }
    finally { setEnrichingAll(false); }
  };

  const updateOutreachField = (field, value) => {
    if (!detail) return;
    setDetail({ ...detail,
      ai_outreach_draft: { ...(detail.ai_outreach_draft || {}), [field]: value } });
    setOutreachDirty(true);
  };

  const saveOutreach = async () => {
    if (!selected || !detail?.ai_outreach_draft) return;
    const d = detail.ai_outreach_draft;
    try {
      await patchOutreachDraft(selected, { subject: d.subject, body: d.body, to: d.to });
      setOutreachDirty(false);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const doSendOutreach = async () => {
    if (!selected) return;
    if (outreachDirty) {
      await saveOutreach();
    }
    const to = detail?.ai_outreach_draft?.to;
    if (!to) return toast.error("Recipient email is empty");
    if (!window.confirm(`Send outreach email to ${to}?`)) return;
    setSending(true);
    try {
      const r = await sendOutreach(selected);
      if (r.ok) {
        toast.success("Sent — prospect marked as contacted");
        const fresh = await getProspect(selected);
        setDetail(fresh);
        setRows((rows) => rows ? {
          ...rows,
          items: rows.items.map((x) => x.id === selected ? { ...x, status: "contacted" } : x),
        } : rows);
      } else {
        toast.error("Send failed — check backend logs");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const doExport = async () => {
    try {
      await downloadProspectsCsv({
        status: status || undefined,
        source: source || undefined,
        has_email: hasEmail === "" ? undefined : hasEmail === "yes",
      });
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const sourceOptions = useMemo(() => (stats?.by_source || []), [stats]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-prospects-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link to="/admin"
                  className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2"
                  data-testid="prospects-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to Admin
            </Link>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              Business Prospecting Reports
            </h1>
            <p className="text-slate-500 mt-1 max-w-2xl text-sm">
              Every visitor who runs a public tool (Free SEO Audit, Growth
              Advisor, Industry pages, etc.) becomes a prospect record. We
              crawl their Contact + About + Footer + Homepage in the background
              and surface everything you need to reach out.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={doEnrichAllMissing}
                    disabled={enrichingAll}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ring-1 ring-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                    data-testid="prospects-enrich-missing-btn">
              {enrichingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              Enrich all missing
            </button>
            <button onClick={load}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ring-1 ring-slate-200 bg-white hover:bg-slate-50"
                    data-testid="prospects-refresh-btn">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={doExport}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-slate-900 hover:bg-slate-800"
                    data-testid="prospects-export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-6">
          <StatChip label="Total" value={stats?.total} tone="bg-white ring-slate-200" />
          <StatChip label="With email" value={stats?.with_email} tone="bg-emerald-50 text-emerald-700 ring-emerald-200" />
          <StatChip label="With phone" value={stats?.with_phone} tone="bg-sky-50 text-sky-700 ring-sky-200" />
          <StatChip label="New" value={stats?.by_status?.new} tone="bg-white ring-slate-200" />
          <StatChip label="Contacted" value={stats?.by_status?.contacted} tone="bg-amber-50 text-amber-700 ring-amber-200" />
          <StatChip label="Qualified" value={stats?.by_status?.qualified} tone="bg-indigo-50 text-indigo-700 ring-indigo-200" />
          <StatChip label="Won" value={stats?.by_status?.won} tone="bg-emerald-50 text-emerald-700 ring-emerald-200" />
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-2 bg-white rounded-xl ring-1 ring-slate-200 p-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search domain, email, URL…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg ring-1 ring-slate-200 focus:ring-slate-400 outline-none"
              data-testid="prospects-search-input"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 bg-white"
                  data-testid="prospects-filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={source} onChange={(e) => setSource(e.target.value)}
                  className="text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 bg-white"
                  data-testid="prospects-filter-source">
            <option value="">All sources</option>
            {sourceOptions.map((s) => (
              <option key={s.source} value={s.source}>
                {s.source} ({s.count})
              </option>
            ))}
          </select>
          <select value={hasEmail} onChange={(e) => setHasEmail(e.target.value)}
                  className="text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 bg-white"
                  data-testid="prospects-filter-hasemail">
            <option value="">Any email state</option>
            <option value="yes">Has email</option>
            <option value="no">No email</option>
          </select>
          <select value={heat} onChange={(e) => setHeat(e.target.value)}
                  className="text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 bg-white"
                  data-testid="prospects-filter-heat">
            <option value="">All heat tiers</option>
            <option value="hot">🔥 Hot only</option>
            <option value="warm">☀️ Warm only</option>
            <option value="cold">❄️ Cold only</option>
          </select>
          <button onClick={load}
                  className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="prospects-apply-filters">
            <Filter className="w-4 h-4" /> Apply
          </button>
        </div>

        {/* List */}
        <div className="mt-4 bg-white ring-1 ring-slate-200 rounded-xl overflow-hidden">
          {rows === null && (
            <div className="p-10 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading prospects…
            </div>
          )}
          {rows?.items?.length === 0 && (
            <div className="p-10 text-center text-slate-500 text-sm">
              No prospect reports yet. They'll appear here the moment a visitor
              uses the Free SEO Audit or Growth Advisor.
            </div>
          )}
          {rows?.items?.length > 0 && (
            <div className="divide-y divide-slate-100">
              {rows.items.map((p) => (
                <div key={p.id}
                     className="p-4 flex flex-wrap items-start gap-3 hover:bg-slate-50/60 cursor-pointer"
                     onClick={() => openDetail(p.id)}
                     data-testid={`prospect-row-${p.id}`}>
                  <div className="w-14 shrink-0 flex flex-col items-center">
                    <div className={`text-lg font-bold tabular-nums leading-none ${
                      p.heat_tier === "hot" ? "text-rose-600"
                      : p.heat_tier === "warm" ? "text-amber-600" : "text-slate-400"}`}
                          data-testid={`prospect-heat-${p.id}`}>
                      {p.heat_score ?? 0}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest font-bold mt-0.5 flex items-center gap-0.5">
                      {p.heat_tier === "hot" ? <span className="text-rose-600">🔥 hot</span>
                       : p.heat_tier === "warm" ? <span className="text-amber-600">☀️ warm</span>
                       : <span className="text-slate-400">❄️ cold</span>}
                    </div>
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <span className="font-medium text-slate-900">{p.domain}</span>
                      <StatusPill status={p.status} />
                      {p.ai_hook && (
                        <span className="text-[10px] uppercase tracking-widest text-indigo-700 bg-indigo-50 ring-1 ring-indigo-100 rounded-full px-1.5 py-0.5 font-bold inline-flex items-center gap-0.5">
                          <Sparkles className="w-2.5 h-2.5" /> AI
                        </span>
                      )}
                    </div>
                    {p.ai_hook ? (
                      <div className="mt-1 text-sm text-slate-700 leading-snug italic max-w-2xl"
                            data-testid={`prospect-hook-${p.id}`}>
                        “{p.ai_hook}”
                      </div>
                    ) : null}
                    <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                      <span>Source: <b className="text-slate-700">{p.last_source || "—"}</b></span>
                      <span>Scans: {p.scan_count || 1}</span>
                      <span>Updated: {new Date(p.updated_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="min-w-[220px]">
                    {(p.emails || [])[0] && (
                      <div className="text-sm text-slate-700 flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5 text-emerald-600" />
                        <a href={`mailto:${p.emails[0]}`}
                           onClick={(e) => e.stopPropagation()}
                           className="hover:underline">{p.emails[0]}</a>
                        {p.emails.length > 1 && (
                          <span className="text-xs text-slate-400">+{p.emails.length - 1}</span>
                        )}
                      </div>
                    )}
                    {(p.phones || [])[0] && (
                      <div className="text-sm text-slate-700 flex items-center gap-1 mt-1">
                        <Phone className="w-3.5 h-3.5 text-sky-600" />
                        {p.phones[0]}
                        {p.phones.length > 1 && (
                          <span className="text-xs text-slate-400">+{p.phones.length - 1}</span>
                        )}
                      </div>
                    )}
                    {!(p.emails || [])[0] && !(p.phones || [])[0] && (
                      <span className="text-xs italic text-slate-400">No contact captured</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={p.status || "new"}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => changeStatus(p.id, e.target.value)}
                            disabled={busyId === p.id}
                            className="text-xs rounded-lg ring-1 ring-slate-200 px-2 py-1.5 bg-white"
                            data-testid={`prospect-status-${p.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex justify-end"
             onClick={closeDetail} data-testid="prospect-drawer">
          <div className="w-full max-w-2xl bg-white shadow-xl h-full overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 flex items-start justify-between sticky top-0 bg-white">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">Prospect</div>
                <h2 className="text-xl font-semibold text-slate-900">
                  {detail?.domain || "…"}
                </h2>
                {detail?.root_url && (
                  <a href={detail.root_url} target="_blank" rel="noreferrer"
                     className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1 mt-0.5">
                    Open site <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                {detail && (
                  <button onClick={() => doRecrawl(detail.id)}
                          className="text-xs px-2.5 py-1.5 rounded-lg ring-1 ring-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"
                          data-testid="prospect-recrawl-btn">
                    <RefreshCw className="w-3.5 h-3.5" /> Recrawl
                  </button>
                )}
                <button onClick={closeDetail}
                        className="p-1.5 rounded-lg hover:bg-slate-100"
                        data-testid="prospect-close-drawer">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-6">
              {!detail && (
                <div className="text-center text-slate-400 py-10">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Loading…
                </div>
              )}
              {detail && (
                <>
                  <div className="flex flex-wrap gap-3">
                    <StatusPill status={detail.status} />
                    <span className="text-xs text-slate-500">
                      Source: <b className="text-slate-700">{detail.last_source}</b>
                    </span>
                    <span className="text-xs text-slate-500">Scans: {detail.scan_count}</span>
                    <span className="text-xs text-slate-500">
                      Created: {new Date(detail.created_at).toLocaleString()}
                    </span>
                  </div>

                  {/* AI Enrichment panel */}
                  <section className="rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 ring-1 ring-indigo-100 p-4"
                            data-testid="prospect-ai-panel">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1">
                        <Sparkles className="w-4 h-4 text-indigo-600" /> AI Enrichment
                      </h3>
                      <button onClick={() => doEnrich(detail.id)}
                              disabled={busyId === detail.id}
                              className="text-xs px-2.5 py-1 rounded-full ring-1 ring-indigo-200 bg-white hover:bg-indigo-50 text-indigo-700 inline-flex items-center gap-1"
                              data-testid="prospect-regen-btn">
                        {busyId === detail.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                        Regenerate
                      </button>
                    </div>
                    {detail.ai_hook ? (
                      <>
                        <p className="text-sm text-slate-800 leading-snug italic"
                            data-testid="prospect-ai-hook-detail">
                          “{detail.ai_hook}”
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                          <span className="text-slate-500">Industry:</span>
                          <b className="text-slate-800">{detail.ai_industry_guess || "—"}</b>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-500">Confidence:</span>
                          <span className={`px-1.5 py-0.5 rounded-full font-semibold ${
                            detail.ai_confidence === "high" ? "bg-emerald-100 text-emerald-700"
                            : detail.ai_confidence === "medium" ? "bg-amber-100 text-amber-700"
                            : "bg-slate-100 text-slate-600"}`}>
                            {detail.ai_confidence || "—"}
                          </span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-500">Source: {detail.ai_source || "—"}</span>
                        </div>
                        {(detail.ai_pains || []).length > 0 && (
                          <ul className="mt-3 space-y-1">
                            {detail.ai_pains.map((p, i) => (
                              <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                                <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" /> {p}
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    ) : (
                      <p className="text-xs italic text-slate-500">
                        Not enriched yet — tap Regenerate to have AI describe why this prospect needs us.
                      </p>
                    )}
                  </section>

                  {/* Outreach Draft panel */}
                  <section className="rounded-2xl ring-1 ring-slate-200 bg-white p-4"
                            data-testid="prospect-outreach-panel">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1">
                        <Send className="w-4 h-4 text-emerald-600" /> Draft Outreach Email
                      </h3>
                      <span className="text-[11px] text-slate-400">
                        Status: <b className="text-slate-700 uppercase">{detail.ai_outreach_draft?.status || "no draft"}</b>
                      </span>
                    </div>
                    {detail.ai_outreach_draft ? (
                      <>
                        <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-1">
                          To
                          <input value={detail.ai_outreach_draft.to || ""}
                                  onChange={(e) => updateOutreachField("to", e.target.value)}
                                  placeholder="prospect@example.com"
                                  className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal normal-case tracking-normal text-slate-900"
                                  data-testid="outreach-to-input" />
                        </label>
                        <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-3">
                          Subject
                          <input value={detail.ai_outreach_draft.subject || ""}
                                  onChange={(e) => updateOutreachField("subject", e.target.value)}
                                  className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal normal-case tracking-normal text-slate-900"
                                  data-testid="outreach-subject-input" />
                        </label>
                        <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-3">
                          Body
                          <textarea value={detail.ai_outreach_draft.body || ""}
                                    onChange={(e) => updateOutreachField("body", e.target.value)}
                                    rows={12}
                                    className="mt-1 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 font-normal normal-case tracking-normal text-slate-900 leading-relaxed font-mono"
                                    data-testid="outreach-body-input" />
                        </label>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <button onClick={saveOutreach}
                                  disabled={!outreachDirty}
                                  className="text-xs px-3 py-1.5 rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40"
                                  data-testid="outreach-save-btn">
                            Save draft
                          </button>
                          <button onClick={doSendOutreach}
                                  disabled={sending || !detail.ai_outreach_draft.to}
                                  className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-40"
                                  data-testid="outreach-send-btn">
                            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            {sending ? "Sending…" : "Approve & Send"}
                          </button>
                          {detail.ai_outreach_draft.sent_at && (
                            <span className="text-[11px] text-emerald-600">
                              Sent {new Date(detail.ai_outreach_draft.sent_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <p className="text-xs italic text-slate-500">
                        No draft yet — enrich the prospect first to auto-generate one.
                      </p>
                    )}
                  </section>

                  {/* Emails */}
                  <section>
                    <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1">
                      <Mail className="w-4 h-4 text-emerald-600" /> Emails ({(detail.emails || []).length})
                    </h3>
                    {(detail.emails || []).length === 0 ? (
                      <p className="text-xs italic text-slate-400">None captured</p>
                    ) : (
                      <ul className="space-y-1">
                        {detail.emails.map((e) => (
                          <li key={e} className="text-sm text-slate-700">
                            <a href={`mailto:${e}`} className="hover:underline text-emerald-700">{e}</a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  {/* Phones */}
                  <section>
                    <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1">
                      <Phone className="w-4 h-4 text-sky-600" /> Phones ({(detail.phones || []).length})
                    </h3>
                    {(detail.phones || []).length === 0 ? (
                      <p className="text-xs italic text-slate-400">None captured</p>
                    ) : (
                      <ul className="flex flex-wrap gap-2">
                        {detail.phones.map((p) => (
                          <li key={p} className="text-sm px-2 py-1 rounded-lg bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                            {p}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  {/* Addresses */}
                  <section>
                    <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-amber-600" /> Addresses ({(detail.addresses || []).length})
                    </h3>
                    {(detail.addresses || []).length === 0 ? (
                      <p className="text-xs italic text-slate-400">None captured</p>
                    ) : (
                      <ul className="space-y-1">
                        {detail.addresses.map((a, i) => (
                          <li key={i} className="text-sm text-slate-700">{a}</li>
                        ))}
                      </ul>
                    )}
                  </section>

                  {/* Socials */}
                  <section>
                    <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1">
                      <Globe className="w-4 h-4 text-indigo-600" /> Social profiles ({(detail.socials || []).length})
                    </h3>
                    {(detail.socials || []).length === 0 ? (
                      <p className="text-xs italic text-slate-400">None captured</p>
                    ) : (
                      <ul className="flex flex-wrap gap-2">
                        {detail.socials.map((s, i) => (
                          <li key={i}>
                            <a href={s.url} target="_blank" rel="noreferrer"
                               className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 hover:bg-indigo-100 inline-flex items-center gap-1">
                              {s.kind} <ExternalLink className="w-3 h-3" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  {/* People */}
                  <section>
                    <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1">
                      <Users className="w-4 h-4 text-fuchsia-600" /> People ({(detail.people || []).length})
                    </h3>
                    {(detail.people || []).length === 0 ? (
                      <p className="text-xs italic text-slate-400">None captured</p>
                    ) : (
                      <ul className="space-y-1">
                        {detail.people.map((p, i) => (
                          <li key={i} className="text-sm text-slate-700">
                            <b>{p.name}</b> <span className="text-slate-500">— {p.role}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  {/* Pages crawled */}
                  <section>
                    <h3 className="text-sm font-semibold text-slate-800 mb-2">
                      Pages crawled ({(detail.pages || []).length})
                    </h3>
                    <ul className="space-y-0.5">
                      {(detail.pages || []).map((p, i) => (
                        <li key={i} className="text-xs text-slate-600 flex items-center gap-1">
                          <span className="font-mono text-slate-400">{p.status}</span>
                          <a href={p.url} target="_blank" rel="noreferrer"
                             className="hover:underline">{p.path}</a>
                          {p.title && <span className="text-slate-400">— {p.title}</span>}
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Notes */}
                  <section>
                    <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1">
                      <MessageSquare className="w-4 h-4 text-slate-500" /> Outreach notes
                    </h3>
                    <ul className="space-y-2 mb-3">
                      {(detail.notes || []).map((n, i) => (
                        <li key={i} className="text-sm bg-slate-50 rounded-lg p-2 ring-1 ring-slate-100">
                          <div className="text-slate-700">{n.text}</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {n.by} · {new Date(n.at).toLocaleString()}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-2">
                      <input value={note} onChange={(e) => setNote(e.target.value)}
                             onKeyDown={(e) => e.key === "Enter" && doAddNote()}
                             placeholder="Add a note (e.g. Left voicemail Tue 3pm)…"
                             className="flex-1 text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2"
                             data-testid="prospect-note-input" />
                      <button onClick={doAddNote}
                              className="text-sm px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                              data-testid="prospect-note-save">Add</button>
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
